import { BaseApiService } from '../BaseApiService';
import { ApiResponse } from '../ApiResponse';
import {
  ApplicationListRequest,
  ApplicationListResponse,
  JobApplicationListResponse,
  JobApplicationDetail,
  DashboardSummaryCounts,
  ApplicationTimeMetrics,
  MentorshipSummaryCounts,
  MentorshipApplicationListResponse,
  StatusWiseCounts
} from '../types/application.types';
import { RecommendedInternship, RecommendedJob, SimilarSkilledInternship } from '../types/internship.types';

class DashboardService extends BaseApiService {

  /**
   * Get applications list with filters and pagination
   */
  static async getApplicationsList(request: ApplicationListRequest): Promise<ApiResponse<ApplicationListResponse>> {
    try {
      const response = await DashboardService.postAuthenticated(
        '/dashboard/applications/list',
        request,
        'Get Applications List'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch applications list');
    }
  }

  /**
   * Get internship summary counts for dashboard
   */
  static async getInternshipsSummaryCounts(): Promise<ApiResponse<DashboardSummaryCounts>> {
    try {
      const response = await DashboardService.getAuthenticated(
        '/dashboard/internships/summarycounts',
        'Get Internships Summary Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch internships summary counts');
    }
  }

  /**
   * Get mentorship summary counts for dashboard
   */
  static async getMentorshipSummaryCounts(): Promise<ApiResponse<MentorshipSummaryCounts>> {
    try {
      const response = await DashboardService.getAuthenticated(
        '/mentorship/summarycounts',
        'Get Mentorship Summary Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch mentorship summary counts');
    }
  }







  /**
   * Get internship status-wise counts
   */
  static async getInternshipsStatusWiseCounts(statusCodes: string[]): Promise<ApiResponse<StatusWiseCounts[]>> {
    try {
      const response = await DashboardService.postAuthenticated(
        '/dashboard/internships/statuswisecounts',
        statusCodes,
        'Get Internships Status Wise Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch internships status wise counts');
    }
  }

  /**
   * Get application summary counts for dashboard
   */
  static async getApplicationSummaryCounts(): Promise<ApiResponse<DashboardSummaryCounts>> {
    try {
      const response = await DashboardService.getAuthenticated(
        '/dashboard/applications/summarycounts',
        'Get Application Summary Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch application summary counts');
    }
  }

  static async getApplicationTimeMetricsForOrg(): Promise<ApiResponse<ApplicationTimeMetrics>> {
    try {
      return await DashboardService.getAuthenticated(
        '/dashboard/applications/org/timemetrics',
        'Get Application Time Metrics'
      );
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch application time metrics');
    }
  }

  /**
   * Get application status-wise counts
   */
  static async getApplicationStatusWiseCounts(statusCodes: string[]): Promise<ApiResponse<StatusWiseCounts[]>> {
    try {
      const response = await DashboardService.postAuthenticated(
        '/dashboard/applications/statuswisecounts',
        statusCodes,
        'Get Application Status Wise Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch application status wise counts');
    }
  }

  /**
   * Update application status
   */
  static async updateApplicationStatus(
    applicationId: string,
    status: string,
    comments?: string
  ): Promise<ApiResponse<string>> {
    try {
      const response = await DashboardService.postAuthenticated(
        '/dashboard/application/update/status',
        { applicationId, status, comments },
        'Update Application Status'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update application status');
    }
  }

  /**
   * Get application details by ID
   */
  static async getApplicationById(applicationId: string): Promise<ApiResponse<any>> {
    try {
      const response = await DashboardService.getAuthenticated(
        `/dashboard/application/${applicationId}/details`,
        'Get Application Details'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch application details');
    }
  }

  /**
   * Update application details
   */
  static async updateApplicationDetails(applicationId: string, applicationData: any): Promise<ApiResponse<any>> {
    try {
      const response = await DashboardService.putAuthenticated(
        `/dashboard/application/${applicationId}/update`,
        applicationData,
        'Update Application Details'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update application details');
    }
  }

  /**
   * Get application summary counts for student dashboard
   */
  static async getApplicationSummaryCountsByStudent(): Promise<ApiResponse<DashboardSummaryCounts>> {
    try {
      const response = await DashboardService.getAuthenticated(
        '/dashboard/applications/student/summarycounts',
        'Get Student Application Summary Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch student application summary counts');
    }
  }

  /**
   * Get applications list for student with filters and pagination
   */
  static async getApplicationsListForStudent(request: ApplicationListRequest): Promise<ApiResponse<ApplicationListResponse>> {
    try {
      const response = await DashboardService.postAuthenticated(
        '/dashboard/applications/student/list',
        request,
        'Get Student Applications List'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch student applications list');
    }
  }

  /**
   * Get job application summary counts for student dashboard
   */
  static async getJobApplicationSummaryCountsByStudent(): Promise<ApiResponse<DashboardSummaryCounts>> {
    try {
      const response = await DashboardService.getAuthenticated(
        '/dashboard/jobs/applications/student/summarycounts',
        'Get Student Job Application Summary Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch student job application summary counts');
    }
  }

  static async getJobApplicationSummaryCountsForOrg(): Promise<ApiResponse<DashboardSummaryCounts>> {
    try {
      const response = await DashboardService.getAuthenticated(
        '/dashboard/jobs/applications/org/summarycounts',
        'Get Organization Job Application Summary Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch organization job application summary counts');
    }
  }

  static async getJobApplicationTimeMetricsForOrg(): Promise<ApiResponse<ApplicationTimeMetrics>> {
    try {
      return await DashboardService.getAuthenticated(
        '/dashboard/jobs/applications/org/timemetrics',
        'Get Organization Job Application Time Metrics'
      );
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch organization job application time metrics');
    }
  }

  static async getJobApplicationStatusWiseCountsForOrg(statusCodes: string[]): Promise<ApiResponse<StatusWiseCounts[]>> {
    try {
      const response = await DashboardService.postAuthenticated(
        '/dashboard/jobs/applications/org/statuswisecounts',
        statusCodes,
        'Get Organization Job Application Status Wise Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch organization job application status counts');
    }
  }

  static async getJobStatusWiseCountsForOrg(statusCodes: string[]): Promise<ApiResponse<StatusWiseCounts[]>> {
    try {
      const response = await DashboardService.postAuthenticated(
        '/dashboard/jobs/statuswisecounts',
        statusCodes,
        'Get Organization Job Status Wise Counts'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch organization job status counts');
    }
  }

  /**
   * Get job applications list for student with filters and pagination
   */
  static async getJobApplicationsListForStudent(request: ApplicationListRequest): Promise<ApiResponse<JobApplicationListResponse>> {
    try {
      const response = await DashboardService.postAuthenticated(
        '/dashboard/jobs/applications/student/list',
        request,
        'Get Student Job Applications List'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch student job applications list');
    }
  }

  /**
   * Get job application details for student view page
   */
  static async getJobApplicationDetails(applicationId: string): Promise<ApiResponse<JobApplicationDetail>> {
    try {
      const response = await DashboardService.getAuthenticated(
        `/dashboard/jobs/applications/student/${encodeURIComponent(applicationId)}`,
        'Get Student Job Application Details'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch job application details');
    }
  }

  /**
   * Get mentorships applications list for student with filters and pagination
   */
  static async getMentorApplicationsListForStudent(request?: Partial<ApplicationListRequest>): Promise<ApiResponse<MentorshipApplicationListResponse>> {
    try {
      // Construct query params from request
      const queryParams: Record<string, any> = {};

      if (request) {
        if (request.page) queryParams.page = request.page;
        if (request.limit) queryParams.size = request.limit;
        if (request.sortBy) queryParams.sortBy = request.sortBy;
        if (request.sortOrder) queryParams.sortOrder = request.sortOrder;

        // Flatten search filters if present
        if (request.searchFilters) {
          Object.entries(request.searchFilters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              queryParams[key] = value;
            }
          });
        }
      }

      // Check if we have any query params
      if (Object.keys(queryParams).length > 0) {
        return await DashboardService.getAuthenticatedWithQuery(
          '/mentorship/applications/list',
          queryParams,
          'Get Mentorship Applications List'
        );
      } else {
        return await DashboardService.getAuthenticated(
          '/mentorship/applications/list',
          'Get Mentorship Applications List'
        );
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch mentorship applications list');
    }
  }

  /**
   * Get recommended internships for student
   */
  static async getRecommendedInternships(): Promise<ApiResponse<RecommendedInternship[]>> {
    try {
      const response = await DashboardService.getAuthenticated(
        `/dashboard/internships/recommended`,
        'Get Recommended Internships'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch recommended internships');
    }
  }

  /**
   * Get recommended jobs for student based on skills, education, and experience
   */
  static async getRecommendedJobs(): Promise<ApiResponse<RecommendedJob[]>> {
    try {
      const response = await DashboardService.getAuthenticated(
        `/dashboard/jobs/recommended`,
        'Get Recommended Jobs'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch recommended jobs');
    }
  }

  /**
   * Get similar skilled students internships
   */
  static async getSimilarSkilledInternships(): Promise<ApiResponse<SimilarSkilledInternship[]>> {
    try {
      const response = await DashboardService.getAuthenticated(
        `/dashboard/internships/similar-skilled`,
        'Get Similar Skilled Internships'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch similar skilled internships');
    }
  }

  /**
   * Get detailed application data including eligibility and skills
   */
  static async getApplicationDetails(applicationId: string): Promise<ApiResponse<any>> {
    try {
      // Pass the applicationId as-is to the backend (it can handle both UUID and numeric formats)
      const response = await DashboardService.getAuthenticated(
        `/dashboard/application/${applicationId}/details`,
        'Get Application Details'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch application details');
    }
  }

  /**
   * Get booking details with booking id for an mentorship application
   */
  static async getMentorshipBookingDetails(bookingId: string): Promise<ApiResponse<any>> {
    try {
      const response = await DashboardService.getAuthenticated(
        `/mentorship/application/${bookingId}/get`,
        'Get mentorship booking details'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get mentorship booking details');
    }
  }

  /**
   * Resubmit application with updated details and status change
   */
  static async resubmitApplication(applicationId: string, applicationData: any): Promise<ApiResponse<string>> {
    try {
      console.log('🔄 [DashboardService] Resubmitting application:', applicationId);
      console.log('📦 [DashboardService] Application data:', applicationData);
      console.log('🌐 [DashboardService] Endpoint URL:', `/dashboard/application/${applicationId}/resubmit`);

      const response = await DashboardService.putAuthenticated(
        `/dashboard/application/${applicationId}/resubmit`,
        applicationData,
        'Resubmit Application'
      );

      console.log('✅ [DashboardService] Resubmit response:', response);
      return response;
    } catch (error: any) {
      console.error('❌ [DashboardService] Resubmit error:', error);
      console.error('❌ [DashboardService] Error details:', {
        message: error.message,
        response: error.response,
        data: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Failed to resubmit application');
    }
  }

  /**
   * Update mentorship application status (Accept/Reject)
   */
  static async updateMentorshipApplicationStatus(
    bookingId: string,
    status: 'ACCEPTED' | 'REJECTED',
    comments: string
  ): Promise<ApiResponse<any>> {
    try {
      console.log('🔄 [DashboardService] Updating mentorship application status:', bookingId, status);

      const response = await DashboardService.putAuthenticated(
        `/mentorship/application/${bookingId}/status/update`,
        { status, comments },
        'Update Mentorship Application Status'
      );

      console.log('✅ [DashboardService] Status update response:', response);
      return response;
    } catch (error: any) {
      console.error('❌ [DashboardService] Status update error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update mentorship application status');
    }
  }

  /**
   * Update mentorship application payment status
   */
  static async updateMentorshipApplicationPaymentStatus(
    bookingId: string,
    status: 'CONFIRMED' | 'PENDING' | 'FAILED',
    comments: string
  ): Promise<ApiResponse<any>> {
    try {
      console.log('🔄 [DashboardService] Updating mentorship application payment status:', bookingId, status);

      const response = await DashboardService.putAuthenticated(
        `/mentorship/application/${bookingId}/payment/status/update`,
        { applicationId: bookingId, status, comments },
        'Update Mentorship Application Status'
      );

      console.log('✅ [DashboardService] Payment Status update response:', response);
      return response;
    } catch (error: any) {
      console.error('❌ [DashboardService] Payment Status update error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update mentorship application status');
    }
  }

}

export default DashboardService;
